import { Collapse, Dropdown } from "bootstrap";

const siteHeader = document.querySelector("[data-site-header]");

if (siteHeader) {
  const updateHeader = () => {
    const scrollPosition = window.scrollY;

    if (scrollPosition > 56) {
      siteHeader.classList.add("is-scrolled");
    } else if (scrollPosition < 40) {
      siteHeader.classList.remove("is-scrolled");
    }
  };

  updateHeader();
  window.addEventListener("scroll", updateHeader, { passive: true });
}

const revealItems = document.querySelectorAll("[data-reveal]");
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

if (revealItems.length > 0) {
  document.body.classList.add("reveal-ready");

  if (reduceMotion || !("IntersectionObserver" in window)) {
    revealItems.forEach((item) => item.classList.add("is-revealed"));
  } else {
    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: "0px 0px -8% 0px",
        threshold: 0.08,
      },
    );

    revealItems.forEach((item) => revealObserver.observe(item));
  }
}

const mobileNavigation = document.querySelector("#primaryNavigation");

if (mobileNavigation) {
  mobileNavigation.querySelectorAll("a:not(.dropdown-toggle)").forEach((link) => {
    link.addEventListener("click", () => {
      if (window.innerWidth >= 1200 || !mobileNavigation.classList.contains("show")) {
        return;
      }

      Collapse.getOrCreateInstance(mobileNavigation).hide();
    });
  });
}

document.querySelectorAll("[data-visual-studio]").forEach((studio) => {
  const tabs = [...studio.querySelectorAll("[data-visual-tab]")];
  const panels = [...studio.querySelectorAll("[data-visual-panel]")];

  if (tabs.length === 0 || panels.length === 0) {
    return;
  }

  const activateTab = (tab, moveFocus = false) => {
    const target = tab.dataset.visualTab;

    if (!target) {
      return;
    }

    tabs.forEach((option) => {
      const isActive = option === tab;
      option.classList.toggle("is-active", isActive);
      option.setAttribute("aria-selected", isActive ? "true" : "false");
      option.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.visualPanel !== target;
    });

    if (moveFocus) {
      tab.focus();
    }
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => activateTab(tab));
    tab.addEventListener("keydown", (event) => {
      let nextIndex = null;

      if (event.key === "ArrowRight") {
        nextIndex = (index + 1) % tabs.length;
      } else if (event.key === "ArrowLeft") {
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = tabs.length - 1;
      }

      if (nextIndex === null) {
        return;
      }

      event.preventDefault();
      activateTab(tabs[nextIndex], true);
    });
  });
});

document.querySelectorAll("[data-tint-simulator]").forEach((simulator) => {
  const stage = simulator.querySelector(".tint-stage");
  const output = simulator.querySelector("[data-tint-output]");
  const buttons = simulator.querySelectorAll("[data-tint-value]");

  if (!stage || !output || buttons.length === 0) {
    return;
  }

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      const value = button.dataset.tintValue;
      const opacity = button.dataset.tintOpacity;

      if (!value || !opacity) {
        return;
      }

      stage.style.setProperty("--tint-opacity", opacity);
      output.textContent = `${value}% VLT`;

      buttons.forEach((option) => {
        const isActive = option === button;
        option.classList.toggle("is-active", isActive);
        option.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    });
  });
});

document.querySelectorAll("[data-ppf-explorer]").forEach((explorer) => {
  const output = explorer.querySelector("[data-ppf-output]");
  const description = explorer.querySelector("[data-ppf-description]");
  const images = explorer.querySelectorAll("[data-ppf-image]");
  const buttons = explorer.querySelectorAll("[data-ppf-state]");

  if (!output || !description || images.length === 0 || buttons.length === 0) {
    return;
  }

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      const state = button.dataset.ppfState;

      if (!state) {
        return;
      }

      images.forEach((image) => {
        image.classList.toggle("is-active", image.dataset.ppfImage === state);
      });

      output.textContent = button.dataset.ppfLabel || "";
      description.textContent = button.dataset.ppfCopy || "";

      buttons.forEach((option) => {
        const isActive = option === button;
        option.classList.toggle("is-active", isActive);
        option.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    });
  });
});

document.querySelectorAll("[data-treatment-switcher]").forEach((switcher) => {
  const output = switcher.querySelector("[data-treatment-output]");
  const description = switcher.querySelector("[data-treatment-description]");
  const images = switcher.querySelectorAll("[data-treatment-image]");
  const buttons = switcher.querySelectorAll("[data-treatment-state]");

  if (!output || !description || images.length === 0 || buttons.length === 0) {
    return;
  }

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      const state = button.dataset.treatmentState;

      if (!state) {
        return;
      }

      images.forEach((image) => {
        image.classList.toggle("is-active", image.dataset.treatmentImage === state);
      });

      output.textContent = button.dataset.treatmentLabel || "";
      description.textContent = button.dataset.treatmentCopy || "";

      buttons.forEach((option) => {
        const isActive = option === button;
        option.classList.toggle("is-active", isActive);
        option.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    });
  });
});

const academyCourseSelect = document.querySelector("#academy-course");
const windowTintNotice = document.querySelector("#academy-window-tint-notice");

if (academyCourseSelect && windowTintNotice) {
  const updateAcademyAvailability = () => {
    windowTintNotice.hidden = academyCourseSelect.value !== "window-tinting";
  };

  academyCourseSelect.addEventListener("change", updateAcademyAvailability);
  updateAcademyAvailability();

  document.querySelectorAll("[data-academy-course-link]").forEach((link) => {
    link.addEventListener("click", () => {
      const course = link.dataset.academyCourseLink;

      if (!course) {
        return;
      }

      academyCourseSelect.value = course;
      updateAcademyAvailability();
    });
  });
}

window.DriveTreat = Object.freeze({
  Collapse,
  Dropdown,
});
